/* Moments Etched v9.7.8 — cart-authoritative project restore, cross-line artwork hydration, neck-label persistence, and Studio-stage synchronization. */
(() => {
  'use strict';

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const dispatch = (element, type = 'change') => element?.dispatchEvent(new Event(type, { bubbles: true }));

  function property(item, name) {
    return item?.properties?.[name] ?? '';
  }

  function showNotice(designer, title, message, error = false) {
    let notice = document.querySelector('[data-me-edit-restore-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.dataset.meEditRestoreNotice = 'true';
      notice.className = 'me-edit-restore-notice';
      const host = document.querySelector('.me-pp__summary-column') || designer.parentElement;
      host?.prepend(notice);
    }
    notice.classList.toggle('is-error', error);
    notice.innerHTML = `<span aria-hidden="true">${error ? '!' : '✎'}</span><span><strong>${title}</strong>${message}</span>`;
  }

  function parseDistribution(value) {
    return String(value || '').split(',').reduce((result, part) => {
      const match = part.trim().match(/^(.+?):\s*(\d+)$/);
      if (match) result[match[1].trim()] = Number(match[2]);
      return result;
    }, {});
  }

  function findByValue(elements, value, keys = []) {
    const wanted = normalize(value);
    return elements.find((element) => keys.some((key) => normalize(element.dataset?.[key] ?? element[key] ?? element.textContent) === wanted));
  }

  async function fetchAsFile(url, fallbackName) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(`Artwork could not be restored (${response.status}).`);
    const blob = await response.blob();
    const nameFromUrl = decodeURIComponent(String(url).split('/').pop()?.split('?')[0] || '');
    return new File([blob], nameFromUrl || fallbackName, { type: blob.type || 'image/png' });
  }

  async function assignArtwork(designer, placement, url) {
    if (!url) return false;

    // Placement artwork cards are rebuilt dynamically after the saved placement
    // selection is restored. Wait for the exact input instead of treating a
    // slow render as lost artwork.
    let input = null;
    for (let attempt = 0; attempt < 24; attempt += 1) {
      input = designer.querySelector(`[data-me-location-input="${CSS.escape(placement)}"]`);
      if (input) break;
      await sleep(100);
    }
    if (!input) return false;

    try {
      const file = await fetchAsFile(url, `${placement.replace(/[^a-z0-9]+/gi, '-')}-artwork.png`);
      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
      dispatch(input, 'change');

      // Wait until the artwork controller marks the restored assignment as
      // hydrated. This keeps Full Back/secondary artwork from disappearing when
      // an edit session opens on a slower browser.
      const card = input.closest?.('[data-me-location-artwork]');
      for (let attempt = 0; attempt < 20; attempt += 1) {
        if (card?.classList.contains('has-artwork')) return true;
        await sleep(100);
      }
      return Boolean(input.files?.length);
    } catch (error) {
      console.warn('[Moments 9.7.8] Artwork restore failed.', placement, error);
      return false;
    }
  }

  async function restoreProject() {
    const params = new URLSearchParams(window.location.search);
    const groupId = params.get('me_edit');
    if (!groupId) return;

    let designer = null;
    let openButton = null;
    for (let attempt = 0; attempt < 40; attempt += 1) {
      designer = document.querySelector('[data-me-designer]');
      openButton = document.querySelector('[data-me-designer-open]');
      if (designer && openButton) break;
      await sleep(100);
    }
    if (!designer || !openButton) {
      console.error('[Moments 9.7.8] Designer controls were not found for cart project restore.');
      return;
    }

    showNotice(designer, 'Loading your personalization', 'Your previous cart selections are being restored.');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('The cart could not be read.');
      const cart = await response.json();
      const lines = (cart.items || []).filter((item) => String(property(item, '_ME Design Group')) === groupId);
      const garments = lines.filter((item) => normalize(property(item, '_ME Line Type')) === 'garment');
      const source = garments[0] || lines[0];
      if (!source) throw new Error('This project is no longer in the cart.');

      designer.dataset.meProjectReference = groupId;
      designer.dataset.meProjectDisplayId = String(property(source, 'Project ID') || '');
      designer.dataset.meEditingCartProject = 'true';

      // v15.4.7.22 — mark cart-project artwork hydration as busy BEFORE the
      // designer opens. Placement cards can otherwise render their normal
      // missing-artwork state for a few seconds while Shopify-hosted files are
      // downloaded and reattached. The Studio uses this flag to show a neutral
      // RESTORING state and temporarily hold Step 3 validation/navigation.
      designer.dataset.meEditRestorePending = 'true';
      window.dispatchEvent(new CustomEvent('me:edit-project-restore-state', {
        detail: { groupId, pending: true }
      }));

      // Open through the normal handler after all designer scripts have loaded.
      openButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      await sleep(250);

      // Defensive fallback: older theme scripts or app overlays can swallow the
      // synthetic click. Ensure the actual modal state is applied directly.
      if (!designer.classList.contains('is-open')) {
        designer.classList.add('is-open');
        designer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('me-designer-open');
        designer.querySelector('[data-me-step-button="design"]')?.click();
      }
      await sleep(500);

      const placements = String(property(source, 'Selected Print Locations'))
        .split(',').map((value) => value.trim()).filter(Boolean);
      const placementInputs = [...designer.querySelectorAll('[data-me-placement-checkbox]')];
      placementInputs.forEach((input) => {
        const checked = placements.includes(input.value);
        if (input.checked !== checked && !input.disabled) {
          input.checked = checked;
          dispatch(input);
        }
      });
      await sleep(250);

      // The current Shopify cart lines are authoritative. Cart-side size edits can
      // change quantities without rewriting legacy line-item properties, so never
      // rebuild an edit session from a stale Size Distribution string when the
      // live project lines already tell us the real quantities.
      const cartDistribution = garments.reduce((result, item) => {
        const size = String(property(item, 'Garment Size') || item.variant_title || '').trim();
        const quantity = Math.max(0, Number(item.quantity || 0));
        if (size && quantity) result[size] = (result[size] || 0) + quantity;
        return result;
      }, {});
      const propertyDistribution = parseDistribution(property(source, 'Size Distribution'));
      const distribution = Object.keys(cartDistribution).length ? cartDistribution : propertyDistribution;
      const orderType = normalize(property(source, 'Order Type'));
      if (orderType.includes('multiple') || Object.keys(distribution).length > 1) {
        const multiButton = designer.querySelector('[data-me-order-mode="multi"]');
        multiButton?.click();
        await sleep(250);
        [...designer.querySelectorAll('[data-me-size-quantity]')].forEach((input) => {
          const size = String(input.dataset.meSizeValue || '').trim();
          input.value = String(distribution[size] || 0);
          dispatch(input, 'input');
          dispatch(input);
        });
      } else {
        const size = property(source, 'Garment Size') || Object.keys(distribution)[0];
        const sizeButton = findByValue([...designer.querySelectorAll('[data-me-size-button]')], size, ['sizeValue']);
        sizeButton?.click();
        const quantityInput = designer.querySelector('[data-me-quantity-input]');
        if (quantityInput) {
          quantityInput.value = String(source.quantity || 1);
          dispatch(quantityInput, 'input');
          dispatch(quantityInput);
        }
      }

      // Restore the exact inside-neck choice before artwork hydration. Defaults
      // are only for new projects; an existing cart project must reopen exactly
      // as the customer left it.
      const neckModeRaw = String(property(source, '_ME Neck Label Mode') || '').trim();
      const insideNeckRaw = String(property(source, 'Inside Neck Label') || '').trim();
      const neckMode = neckModeRaw || (/moments etched/i.test(insideNeckRaw)
        ? 'me-branding'
        : /custom|your brand/i.test(insideNeckRaw)
          ? 'custom'
          : 'original');
      const neckChoice = designer.querySelector(`[data-me-neck-label-choice][value="${CSS.escape(neckMode)}"]`);
      if (neckChoice) {
        neckChoice.checked = true;
        dispatch(neckChoice);
        await sleep(180);
      }
      designer.dataset.meNeckLabelMode = neckMode;

      const notes = designer.querySelector('#me-product-notes');
      if (notes && property(source, 'Personalization Notes')) {
        notes.value = property(source, 'Personalization Notes');
        dispatch(notes, 'input');
      }

      const proof = designer.querySelector('[data-me-proof-checkbox], input[name="properties[Digital Proof Requested]"]');
      if (proof) {
        const proofValue = property(source, 'Digital Proof Requested') || property(source, 'Proof Status');
        proof.checked = /yes|requested/i.test(String(proofValue));
        dispatch(proof);
      }

      // v15.4.7.21 — Shopify-hosted artwork is project data, not garment-line-only
      // data. The staged upload flow copies hosted URLs to the other project lines
      // (including paid placement add-on lines). Cart-side size edits can later
      // rebuild or replace a garment line while those add-on lines still retain the
      // original hosted artwork. Search the ENTIRE design group so a re-edit does
      // not incorrectly mark sleeves/back artwork as missing.
      //
      // A durable _ME Restore Artwork mirror is preferred when present; the normal
      // Shopify file property remains the fallback for projects created before this
      // release. Never substitute a production-preview composite for source artwork.
      const artworkByPlacement = new Map();
      const collectHostedArtwork = (item, mirrorOnly = false) => {
        Object.entries(item?.properties || {}).forEach(([key, value]) => {
          if (typeof value !== 'string') return;
          const url = value.trim();
          if (!/^https?:\/\//i.test(url)) return;

          const mirrorMatch = String(key).match(/^_ME Restore Artwork - (.+)$/i);
          if (mirrorMatch) {
            const placement = mirrorMatch[1].trim();
            if (placement) artworkByPlacement.set(placement, url);
            return;
          }

          if (mirrorOnly) return;
          const artworkMatch = String(key).match(/^_?Artwork - (.+)$/i);
          if (!artworkMatch) return;
          const placement = artworkMatch[1].trim();
          if (placement && !artworkByPlacement.has(placement)) artworkByPlacement.set(placement, url);
        });
      };

      // First pass: durable mirrors from every line win. Second pass: legacy file
      // properties fill any gaps. This also covers projects already in the cart.
      lines.forEach((item) => collectHostedArtwork(item, true));
      lines.forEach((item) => collectHostedArtwork(item, false));

      const artworkEntries = [...artworkByPlacement.entries()]
        .filter(([placement]) => placements.includes(placement))
        .map(([placement, url]) => ({ placement, url }));
      const savedArtworkCount = lines.reduce((max, item) => {
        const count = Math.max(0, Number.parseInt(String(property(item, '_Artwork File Count') || '0'), 10) || 0);
        return Math.max(max, count);
      }, 0);
      const expectedArtworkCount = Math.max(savedArtworkCount, artworkEntries.length);

      let restoredArtwork = 0;
      for (const entry of artworkEntries) {
        if (await assignArtwork(designer, entry.placement, entry.url)) restoredArtwork += 1;
      }

      designer.dataset.meEditRestoreComplete = 'true';
      delete designer.dataset.meEditRestorePending;
      window.dispatchEvent(new CustomEvent('me:edit-project-restore-state', {
        detail: { groupId, pending: false, restoredArtwork, expectedArtwork: expectedArtworkCount }
      }));
      window.dispatchEvent(new CustomEvent('me:edit-project-restored', {
        detail: { groupId, restoredArtwork, expectedArtwork: expectedArtworkCount, neckMode }
      }));

      // Do not force the legacy/native Design step after hydration. The 5-stage
      // Studio owns the customer-visible stage, and a late native step click can
      // re-show Garment/Order controls on top of Step 3 after the customer has
      // already advanced. The me:edit-project-restored event above lets the
      // Studio re-sync whichever stage the customer is currently viewing.
      showNotice(
        designer,
        'Editing your cart project',
        restoredArtwork >= expectedArtworkCount
          ? 'Selections and artwork were restored. Review each step, then choose Update Personalized Item to replace the existing cart project.'
          : 'Selections were restored. One or more artwork files must be selected again before updating the project.'
      );

      const submitLabel = designer.querySelector('[data-me-review-submit] span:first-child, [data-me-review-submit] [data-me-primary-action-label]');
      if (submitLabel) submitLabel.textContent = 'Update Personalized Item';

      params.delete('me_edit');
      const clean = `${location.pathname}${params.toString() ? `?${params}` : ''}${location.hash}`;
      history.replaceState({}, '', clean);
    } catch (error) {
      if (designer) {
        delete designer.dataset.meEditRestorePending;
        window.dispatchEvent(new CustomEvent('me:edit-project-restore-state', {
          detail: { groupId, pending: false, failed: true }
        }));
      }
      console.error('[Moments 9.7.8] Project restore failed.', error);
      showNotice(designer, 'Unable to open this project', error.message || 'Remove the item and personalize it again.', true);
    }
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => setTimeout(restoreProject, 350))
    : setTimeout(restoreProject, 350);
})();
